import type { IEquatable, IMedia } from '@websolutespa/bom-core';
import { BowlBlock } from '@websolutespa/payload-plugin-bowl';
import { TypeWithID } from 'payload';
import { LlmKnowledgeBaseCreationMode } from './consts';

export type LlmChunkLog = Record<string, unknown> & {
  type: 'log';
};

export type LlmChunkError = {
  type: 'error';
  error: string;
};

export type LlmChunkHeader = {
  type: 'header';
  threadId: string;
  messageId: string;
};

export type LlmChunkInfo = {
  type: 'info';
  threadId: string;
};

export type LlmChunkEnd = {
  type: 'end';
};

export type LlmChunkText = {
  type: 'text';
  text: string;
};

export type LlmChunkString = {
  type: 'string';
  content: string;
}; // deprecated

export type LlmChunkTextOrString = LlmChunkText | LlmChunkString;

export function isChunkText(chunk: LlmChunkItem): chunk is LlmChunkText {
  return chunk.type === 'text';
}

export function isChunkString(chunk: LlmChunkItem): chunk is LlmChunkString {
  return chunk.type === 'string';
}

export function isChunkTextOrString(chunk: LlmChunkItem): chunk is LlmChunkTextOrString {
  return chunk.type === 'text' || chunk.type === 'string';
}

export type LlmChunkImage = {
  type: 'image';
  url: string;
};

export type LlmChunkFile = {
  type: 'file';
  url: string;
};

export type ImageUrl = {
  url: string;
};

export type LlmChunkImageUrl = {
  type: 'image_url';
  image_url: ImageUrl;
};

export type LlmChunkKnownItem =
  LlmChunkEnd
  | LlmChunkError
  | LlmChunkFile
  | LlmChunkImage
  | LlmChunkImageUrl
  | LlmChunkHeader
  | LlmChunkInfo
  | LlmChunkLog
  | LlmChunkText
  | LlmChunkString;

// !!! todo move to bom-core

type NotA<T> = T extends LlmChunkKnownItem['type'] ? never : T;
type NotB<T> = LlmChunkKnownItem['type'] extends T ? never : T;

export type LlmChunkUnknownItemType<T> = NotA<T> & NotB<T>;

export type LlmChunkUnknownItem = Omit<Record<string, unknown>, 'type'>;

export type LlmChunkItem = LlmChunkKnownItem;

export type LlmChunk = string
  | LlmChunkItem;

export type LlmMessage = {
  messageId?: string;
  role: string;
  content: string | LlmChunkItem[];
  feedback?: {
    rating?: number;
    notes?: string;
  };
  createdAt?: Date | string;
};

export type LlmSerializedMessage = {
  messageId?: string;
  role: string;
  content: string;
};

export type LlmDecodedMessage = LlmMessage & {
  chunks: LlmChunkItem[];
};

export type StreamResponse = {
  chunks: LlmChunkItem[];
  threadId: string;
  messageId: string;
};

export type LlmUpload = {
  file: File;
  base64?: string;
};

export type LlmUploaded = {
  id: string;
  name: string;
  filename: string;
  mimeType: string;
  filesize: number;
  createdAt: string;
  updatedAt: string;
  url: string;
  type: 'image' | 'file';
};

export type LlmAttachment = {
  type: 'image' | 'file';
  url: string;
};

export type LlmSummary = {
  fullName: string,
  email: string,
  privacy: boolean,
  appId: string,
  threadId: string,
  summaryUrl: string,
  html: string,
};

export type LlmAppReferrer = {
  id: string;
  referrer: string;
};

export type LlmMapReplacedField = {
  srcName: string;
  destName: string;
};

export type LlmMapNewField = {
  name: string;
  value: string;
};

export type LlmMapDeletedField = {
  name: string;
};

export type LlmMapMetaField = {
  name: string;
  description: string;
  type: string;
};

export type LlmFieldsMapping = {
  replacedFields: LlmMapReplacedField[];
  newFields: LlmMapNewField[];
  deletedFields: LlmMapDeletedField[];
  metaFields: LlmMapMetaField[];
};

export type LlmKnowledgeBaseEndpoint = {
  endpointUrl: string;
  authentication: string;
  authSecret: string;
  fieldsMapping: LlmFieldsMapping;
  id: string;
};

export type VectorDb = Record<string, unknown> & {
  id: string;
  filename: string;
  mimeType: string;
  filesize: number;
  createdAt: Date | string;
  updatedAt?: Date | string;
  url: string;
};

export type LlmKnowledgeBaseFile = {
  media: IMedia;
};

export type LlmKnowledgeBase = {
  integrations: BowlBlock[];
  externalEndpoints: LlmKnowledgeBaseEndpoint[];
  files?: LlmKnowledgeBaseFile[];
  vectorDbType?: string;
  vectorDbFile: VectorDb;
  chunkingMethod?: string;
  chunkSize?: number;
  chunkOverlap?: number;
  deepLevel?: number;
};

export type LlmSearchSettings = {
  searchType: string;
  scoreThreshold?: number;
  searchK?: number;
  metadataFilters?: string[];
};

export type LlmChainSettings = {
  model: string;
  prompt: string;
  temperature: number;
  outputStructure?: {
    outputType: 'text' | 'json';
    outputFormat?: Record<string, unknown>;
  };
};

export type LlmDbSettings = {
  connectionString: string;
};

export type LlmApiSettings = {
  endpoint?: string;
  method?: string;
  headers?: Record<string, string>;
  payload?: Record<string, unknown>;
};

export type SharepointSettings = {
  clientId: string;
  clientSecret: string;
  tenantId: string;
  siteId: string;
  driveId: string;
};

export type LlmMcpSettings = {
  mcpUrl?: string;
  mcpToolName?: string;
  mcpAuthToken?: string;
};

export type LlmAppTool = {
  id: string;
  isActive: boolean;
  nightlyKbGeneration: boolean;
  name: string;
  description?: string;
  type: string;
  functionId: string;
  functionName: string;
  functionDescription: string;
  secrets: LlmAppToolSecret[];
  llmChainSettings: LlmChainSettings;
  dataSource: string;
  dbSettings: LlmDbSettings;
  apiSettings: LlmApiSettings;
  mcpSettings?: LlmMcpSettings;
  waitingMessage: string;
  knowledgeBase: LlmKnowledgeBase;
  searchSettings: LlmSearchSettings;
};

export type LlmRules = {
  vector_type: string;
  vectorDbFile: VectorDb;
  threshold: number;
};

export type LlmFineTuning = {
  modelNameSuffix: string;
  fineTunedModelName: string;
  currentJobId?: string;
};

export type LlmAppSettings = {
  credentials: LlmAppCredentials;
  knowledgeBase: LlmKnowledgeBase;
  llmConfig: LlmAppConfig;
  fineTuning: LlmFineTuning;
  appTools: LlmAppTool[];
  rules: LlmRules;
  nightlyKbGeneration: boolean;
};

export type LlmAppCredentials = {
  apiKey: string;
  appKey: string;
  httpReferrers: LlmAppReferrer[];
  previewEnabled?: boolean;
};

export type LlmAppPrompt = {
  systemMessage: string;
};

export type LlmTool = {
  name: string;
  description?: string;
  functionName: string;
  functionDescription: string;
  waitingMessage: string;
};

export type LlmAppSecrets = Record<string, string>;

export type LlmAppToolSecret = Record<string, string>;

export type LlmAppConfig = {
  provider: string;
  model: string;
  temperature: number;
  prompt: {
    // @deprecated Remove systemMessage from LlmAppConfig
    systemMessage: string;
    prompt: LlmAppPrompt;
  };
  secrets: LlmAppSecrets;
  tools: LlmTool[];
  langChainTracing: boolean;
  langChainProject: string;
  outputStructure?: {
    outputType: string;
    outputFormat: object | null;
  };
};

export type LlmAppCard = {
  icon?: IMedia;
  title: string;
  body: string;
  message: string;
};

export type LlmLogo = {
  logo: IMedia;
};

export type LlmSampleInputText = {
  id: string;
  sampleInputText: string;
};

export type LlmHeaderCta = {
  id: string;
  title: string;
  href: string;
  target: string;
  icon?: IMedia;
};

export type LlmRecognitionMode = ('none' | 'default') & string;
export type LlmSynthesisMode = ('none' | 'default' | 'elevenlabs') & string;
export type LlmStorageMode = ('locale' | 'session') & string;

export type LlmAppContents = {
  additionalLogos?: LlmLogo[];
  collapsedWelcomeText?: string;
  collapsedWelcomeTextCta?: string;
  collapsedWelcomeTextHover?: string;
  customIntro?: string;
  customTheme?: {};
  disablePoweredBy?: boolean;
  disableSpeechRecognition?: boolean;
  disableSpeechSynthesis?: boolean;
  disclaimer?: string;
  disclaimerFooter?: boolean;
  disclaimerShort?: string;
  enableFeedback?: boolean;
  enableHistory?: boolean;
  enableMinimize?: boolean;
  enableUpload?: boolean;
  extendedWelcomeText?: string;
  headerCtas?: LlmHeaderCta[];
  intro?: string;
  layoutBuilder?: LlmAppCard[];
  logo?: IMedia;
  logoMini?: IMedia;
  maxFileSize?: number;
  mimeTypes?: string;
  openingSound?: IMedia;
  promptPlaceholder?: string;
  promptTokenLimit?: number;
  recognitionMode?: LlmRecognitionMode;
  sampleInputTexts?: LlmSampleInputText[];
  shortWelcomeText?: string;
  storageMode?: LlmStorageMode;
  synthesisMode?: LlmSynthesisMode;
  textToSpeechApiKey?: string;
  textToSpeechVoiceId?: string;
};

export type LlmWebhook = {
  webhook: string;
};

export type LlmThread = {
  message: LlmMessage[],
} & TypeWithID;

export type LlmInfoThread = {
  messages: LlmMessage[],
  threadId: string;
};

export type LlmInfo = {
  contents: LlmAppContents;
  thread?: LlmInfoThread;
};

export type LlmAppLink = {
  title: string;
  url: string;
};

export type LlmAppLinksGroup = {
  title: string;
  links: LlmAppLink[];
};

export type LlmAppPlugin = {
  name?: string;
  version?: string;
  namespace?: string;
};

export type LlmMode = ('site' | 'chat' | 'url2text' | 'translation') & string;

export type LlmApp = {
  createdAt: Date | string;
  settings: LlmAppSettings;
  id: string;
  isActive: boolean;
  name: string;
  mode: LlmMode;
  updatedAt: Date | string;
  contents: LlmAppContents;
  webhooks: LlmWebhook[];
  linksGroups: LlmAppLinksGroup[];
  plugin?: LlmAppPlugin;
};

export type RobotTaskResult = {
  success: boolean;
  file?: string;
  error?: string;
};

export type RobotTaskMetadataExtra = {
  traceId: string;
  appId: string;
  toolId?: string;
  [key: string]: string | undefined;
};

export type RobotTaskCompleted = {
  status: 'completed';
  result: RobotTaskResult;
};

export type RobotTaskFailed = {
  status: 'failed';
  error: string;
};

export type RobotTaskResponse = {
  id: string;
  type: string;
  metadata: {
    extra?: RobotTaskMetadataExtra;
    [key: string]: string | object | undefined;
  };
} & (RobotTaskCompleted | RobotTaskFailed);

export type RobotTaskRequest = {
  provider: string;
  model: string;
  taskId: string;
  files?: string[];
  dataSource?: string;
  vectorDbType?: string;
  integrations?: BowlBlock[];
  endpoints?: LlmKnowledgeBaseEndpoint[];
  secrets: LlmAppSecrets;
  downloadPath: string;
  chunkingMethod?: string;
  chunkSize?: number;
  chunkOverlap?: number;
  deepLevel?: number;
  metadata: {
    traceId: string;
    appId: string;
    toolId?: string;
    [key: string]: string | undefined;
  };
};

export type LlmTaskLogData = {
  taskStatus?: string;
  taskRequest?: RobotTaskRequest;
  taskResponse?: RobotTaskResponse;
  savedVectorDb?: {};
  error?: string;
};

export type LlmFeedbackRequest = {
  appId: string;
  apiKey: string;
  threadId: string;
  messageId?: string;
  feedbackRating: number;
  feedbackMessage?: string;
};

export type RobotFeedbackRequest = {
  api_key: string;
  provider: string;
  user_id: IEquatable;
  comment: string;
  rating: number;
  anonymize: boolean;
  timestamp: string;
  message_id?: string;
  message_input?: string;
  message_output?: string;
};

export type AppToolOption = {
  value: string;
  label: string;
};

export type RobotEndpointPostProcessor = {
  id: string;
  value: string;
};

export type LlmKnowledgeBaseCreationMode = keyof typeof LlmKnowledgeBaseCreationMode;
export type LlmKnowledgeBaseCreationOptions = {
  mode: LlmKnowledgeBaseCreationMode;
  forceKbGeneration: boolean; // if true, ignore all checks and force the KB generation
  toolFunctionId?: string
};

export type LlmKnowledgeBasePollResponse = {
  processing: boolean;
  success?: boolean;
  errors?: {
    job: string;
    taskStatus: string;
    error: string;
  }[];
};

export type RobotModel = {
  id: string;
  created: number;
  object: string;
  owned_by: string;
};

export type RobotProvider = {
  id: string;
  value: string;
};

/*
export type LlmRulesResult = {
  name: string;
  rules?: LlmRules;
  status: string,
  error?: string,
};
*/

export type LlmRulesResult = {
  name: string;
  vectorDbFile?: string | number;
  status: string,
  error?: string,
};

export type RobotTool = {
  id: string;
  value: string;
};

export type RobotVectorDbType = {
  id: string;
  value: string;
};

export type PythonStreamRequest = {
  mode: string;
  systemMessage: string;
  systemContext: object;
  messages: LlmMessage[];
  provider: string | null;
  model: string | null;
  temperature: number | null;
  secrets: LlmAppSecrets;
  threadId: string;
  msgId: string;
  tools: LlmTool[];
  appTools: PythonStreamTool[];
  vectorDb: string | null;
  rules: {
    vector_db: string | null;
    threshold: number;
  };
  fineTunedModel: string | null;
  langChainTracing: boolean;
  langChainProject: string | null;
  outputStructure: {
    outputType: string;
    outputFormat: object | null;
  } | null;
};

export type PythonStreamLlmChainSettings = {
  prompt: string;
  temperature: number;
};

export type PythonStreamDbSettings = {
  connectionString: string;
  additionalPrompt?: string;
  limit?: number;
};

export type PythonStreamApiSettings = {
  endpoint?: string;
  method?: string;
  headers?: Record<string, string>;
  payload?: Record<string, unknown>;
};

export type PythonStreamTool = {
  name: string;
  description?: string;
  type: string;
  functionId: string;
  functionName: string;
  functionDescription: string;
  model?: string;
  secrets: LlmAppToolSecret[];
  llmChainSettings: PythonStreamLlmChainSettings;
  dataSource: string;
  dbSettings: PythonStreamDbSettings;
  apiSettings: PythonStreamApiSettings;
  searchSettings: LlmSearchSettings;
  integrations?: BowlBlock[];
  endpoints?: LlmKnowledgeBaseEndpoint[];
  waitingMessage: string;
  vectorDbType: string | null;
  vectorDbFile: string | null;
  rulesVectorDb: string | null;
};

export type PythonStreamResponse = Partial<{
  created: number;
  id: string;
  model: string;
  object: string;
  python: string;
  system_fingerprint: string | null;
  threadId: string;
  messageId: string;
  version: string;
}> & {
  chunks: LlmChunkItem[];
};

export type pythonInvokeRequest = {
  provider: string;
  mode: string;
  systemMessage: string;
  systemContext: object;
  messages: LlmMessage[];
  model: string | null;
  secrets: LlmAppSecrets;
  threadId: string;
  tools: LlmTool[];
  appTools: PythonStreamTool[];
  vectorDb: string | null;
  rules: {
    vectorDbFile: string | null;
    threshold: number;
  };
  fineTunedModel: string | null;
  langChainTracing: boolean;
  langChainProject: string | null;
  outputStructure: {
    outputType: string;
    outputFormat: object | null;
  } | null;
};


